import React, { useState, useEffect, useCallback } from 'react';
import { workflowsApi, workflowExecutionApi } from '../lib/api';
import { formatDateTime } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Plus, Lightning, Trash, Play, Pencil, ArrowRight,
  Bell, ClipboardText, ChartLineUp, UserPlus, FlowArrow, CheckCircle, X
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import logger from '../lib/logger';

const TRIGGER_TYPES = [
  { id: 'deal_created', label: 'Deal Created', icon: Lightning, desc: 'Fires when a new deal is created' },
  { id: 'deal_stage_changed', label: 'Deal Stage Changed', icon: ChartLineUp, desc: 'Fires when a deal moves to a new stage' },
  { id: 'contact_created', label: 'Contact Created', icon: UserPlus, desc: 'Fires when a new contact is added' },
  { id: 'task_completed', label: 'Task Completed', icon: CheckCircle, desc: 'Fires when a task is marked complete' },
  { id: 'manual', label: 'Manual Trigger', icon: Play, desc: 'Run this workflow manually' },
];

const ACTION_TYPES = [
  { id: 'create_task', label: 'Create Task', icon: ClipboardText, fields: ['title', 'description', 'priority', 'assigned_to'] },
  { id: 'send_notification', label: 'Send Notification', icon: Bell, fields: ['title', 'message', 'target_user_id'] },
  { id: 'update_deal_stage', label: 'Update Deal Stage', icon: ChartLineUp, fields: ['stage'] },
  { id: 'update_lead_score', label: 'Update Lead Score', icon: UserPlus, fields: ['score_change'] },
];

const DEAL_STAGES = ['lead', 'qualified', 'proposal', 'negotiation', 'closed_won', 'closed_lost'];

export default function WorkflowBuilderPage() {
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [executions, setExecutions] = useState([]);
  const [activeTab, setActiveTab] = useState('workflows');

  // Builder state
  const [builderName, setBuilderName] = useState('');
  const [builderDesc, setBuilderDesc] = useState('');
  const [builderTrigger, setBuilderTrigger] = useState('manual');
  const [builderConditions, setBuilderConditions] = useState({});
  const [builderActions, setBuilderActions] = useState([]);
  const [builderActive, setBuilderActive] = useState(true);
  const [editingWorkflow, setEditingWorkflow] = useState(null);

  const fetchWorkflows = useCallback(async () => {
    try {
      const response = await workflowsApi.getAll({ limit: 100 });
      setWorkflows(response.data);
    } catch (error) {
      toast.error('Failed to fetch workflows');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchWorkflows(); }, [fetchWorkflows]);

  const fetchExecutions = async (workflowId) => {
    try {
      const response = await workflowExecutionApi.getExecutions(workflowId, { limit: 20 });
      setExecutions(response.data);
    } catch (error) {
      logger.error('Failed to fetch executions:', error);
    }
  };

  const resetBuilder = () => {
    setBuilderName('');
    setBuilderDesc('');
    setBuilderTrigger('manual');
    setBuilderConditions({});
    setBuilderActions([]);
    setBuilderActive(true);
    setEditingWorkflow(null);
  };

  const openEditBuilder = (workflow) => {
    setEditingWorkflow(workflow);
    setBuilderName(workflow.name);
    setBuilderDesc(workflow.description || '');
    setBuilderTrigger(workflow.trigger_type);
    setBuilderConditions(workflow.trigger_conditions || {});
    setBuilderActions(workflow.actions || []);
    setBuilderActive(workflow.is_active);
    setIsCreateOpen(true);
  };

  const addAction = () => {
    setBuilderActions([...builderActions, { _key: `act_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, type: 'create_task', config: {} }]);
  };

  const updateAction = (index, field, value) => {
    const updated = [...builderActions];
    if (field === 'type') {
      updated[index] = { type: value, config: {} };
    } else {
      updated[index].config = { ...updated[index].config, [field]: value };
    }
    setBuilderActions(updated);
  };

  const removeAction = (index) => {
    setBuilderActions(builderActions.filter((_, i) => i !== index));
  };

  const handleSaveWorkflow = async () => {
    if (!builderName.trim()) {
      toast.error('Workflow name is required');
      return;
    }
    if (builderActions.length === 0) {
      toast.error('Add at least one action');
      return;
    }

    const payload = {
      name: builderName,
      description: builderDesc,
      trigger_type: builderTrigger,
      trigger_conditions: builderConditions,
      actions: builderActions,
      is_active: builderActive
    };

    try {
      if (editingWorkflow) {
        await workflowsApi.update(editingWorkflow.id, payload);
        toast.success('Workflow updated');
      } else {
        await workflowsApi.create(payload);
        toast.success('Workflow created');
      }
      setIsCreateOpen(false);
      resetBuilder();
      fetchWorkflows();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save workflow');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this workflow?')) return;
    try {
      await workflowsApi.delete(id);
      toast.success('Workflow deleted');
      if (selectedWorkflow?.id === id) setSelectedWorkflow(null);
      fetchWorkflows();
    } catch (error) {
      toast.error('Failed to delete workflow');
    }
  };

  const handleExecute = async (workflow) => {
    try {
      const response = await workflowExecutionApi.execute(workflow.id, {});
      toast.success(`Workflow executed: ${response.data.results.length} actions`);
      fetchExecutions(workflow.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Execution failed');
    }
  };

  const handleToggleActive = async (workflow) => {
    try {
      await workflowsApi.update(workflow.id, {
        ...workflow,
        is_active: !workflow.is_active
      });
      fetchWorkflows();
    } catch (error) {
      toast.error('Failed to toggle workflow');
    }
  };

  const selectWorkflow = (wf) => {
    setSelectedWorkflow(wf);
    fetchExecutions(wf.id);
  };

  const renderActionConfig = (action, index) => {
    const actionDef = ACTION_TYPES.find(a => a.id === action.type);
    if (!actionDef) return null;

    return (
      <div className="space-y-3 mt-3">
        {actionDef.fields.includes('title') && (
          <div className="space-y-1">
            <Label className="text-xs">Title</Label>
            <Input
              value={action.config.title || ''}
              onChange={(e) => updateAction(index, 'title', e.target.value)}
              placeholder="Task or notification title"
              data-testid={`action-title-${index}`}
            />
          </div>
        )}
        {actionDef.fields.includes('description') && (
          <div className="space-y-1">
            <Label className="text-xs">Description</Label>
            <Textarea
              value={action.config.description || ''}
              onChange={(e) => updateAction(index, 'description', e.target.value)}
              rows={2}
              placeholder="Description"
            />
          </div>
        )}
        {actionDef.fields.includes('message') && (
          <div className="space-y-1">
            <Label className="text-xs">Message</Label>
            <Textarea
              value={action.config.message || ''}
              onChange={(e) => updateAction(index, 'message', e.target.value)}
              rows={2}
              placeholder="Notification message"
            />
          </div>
        )}
        {actionDef.fields.includes('priority') && (
          <div className="space-y-1">
            <Label className="text-xs">Priority</Label>
            <Select value={action.config.priority || 'medium'} onValueChange={(v) => updateAction(index, 'priority', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        {actionDef.fields.includes('stage') && (
          <div className="space-y-1">
            <Label className="text-xs">Target Stage</Label>
            <Select value={action.config.stage || 'qualified'} onValueChange={(v) => updateAction(index, 'stage', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {DEAL_STAGES.map(s => <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        {actionDef.fields.includes('score_change') && (
          <div className="space-y-1">
            <Label className="text-xs">Score Change (+/-)</Label>
            <Input
              type="number"
              value={action.config.score_change || 0}
              onChange={(e) => updateAction(index, 'score_change', parseInt(e.target.value) || 0)}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="workflow-builder-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Workflow Automation</h1>
          <p className="text-muted-foreground text-sm mt-1">Build automated processes with triggers and actions</p>
        </div>
        <Button onClick={() => { resetBuilder(); setIsCreateOpen(true); }} className="rounded-sm" data-testid="create-workflow-btn">
          <Plus size={18} className="mr-2" />
          New Workflow
        </Button>
      </div>

      <div className="flex gap-6">
        {/* Workflow List */}
        <div className="w-80 flex-shrink-0 space-y-2">
          {loading ? (
            <div className="text-center p-8 text-muted-foreground">Loading...</div>
          ) : workflows.length === 0 ? (
            <div className="text-center p-8 text-muted-foreground border border-dashed border-border rounded-sm">
              <FlowArrow size={32} className="mx-auto mb-2 opacity-50" />
              <p>No workflows yet</p>
              <p className="text-xs mt-1">Create your first automation</p>
            </div>
          ) : (
            workflows.map((wf) => {
              const TriggerIcon = TRIGGER_TYPES.find(t => t.id === wf.trigger_type)?.icon || Lightning;
              return (
                <div
                  key={wf.id}
                  className={`p-4 rounded-sm border cursor-pointer transition-all ${
                    selectedWorkflow?.id === wf.id
                      ? 'border-primary bg-primary/5'
                      : 'border-border bg-card hover:bg-muted/50'
                  }`}
                  onClick={() => selectWorkflow(wf)}
                  data-testid={`workflow-item-${wf.id}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <TriggerIcon size={18} className="text-primary" />
                      <h3 className="font-medium text-sm truncate">{wf.name}</h3>
                    </div>
                    <Switch
                      checked={wf.is_active}
                      onCheckedChange={() => handleToggleActive(wf)}
                      data-testid={`toggle-wf-${wf.id}`}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-2 mb-2">{wf.description || 'No description'}</p>
                  <div className="flex items-center gap-2">
                    <Badge variant={wf.is_active ? 'default' : 'secondary'} className="text-xs">
                      {wf.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      {wf.actions?.length || 0} actions
                    </Badge>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Detail Panel */}
        <div className="flex-1">
          {selectedWorkflow ? (
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <div className="flex items-center justify-between mb-4">
                <TabsList>
                  <TabsTrigger value="workflows">Design</TabsTrigger>
                  <TabsTrigger value="executions">Execution Log</TabsTrigger>
                </TabsList>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => openEditBuilder(selectedWorkflow)} data-testid="edit-workflow-btn">
                    <Pencil size={14} className="mr-1" /> Edit
                  </Button>
                  {selectedWorkflow.trigger_type === 'manual' && (
                    <Button size="sm" onClick={() => handleExecute(selectedWorkflow)} data-testid="execute-workflow-btn">
                      <Play size={14} className="mr-1" /> Run
                    </Button>
                  )}
                  <Button variant="destructive" size="sm" onClick={() => handleDelete(selectedWorkflow.id)} data-testid="delete-workflow-btn">
                    <Trash size={14} />
                  </Button>
                </div>
              </div>

              <TabsContent value="workflows" className="space-y-4">
                <div className="bg-card border border-border p-6 rounded-sm">
                  <h2 className="font-heading text-xl font-semibold mb-1">{selectedWorkflow.name}</h2>
                  <p className="text-sm text-muted-foreground">{selectedWorkflow.description || 'No description'}</p>
                </div>

                {/* Visual Flow */}
                <div className="space-y-3">
                  {/* Trigger */}
                  <div className="bg-primary/5 border border-primary/20 p-4 rounded-sm">
                    <p className="text-xs tracking-[0.2em] uppercase font-semibold text-primary mb-2">TRIGGER</p>
                    <div className="flex items-center gap-2">
                      <Lightning size={18} className="text-primary" />
                      <span className="font-medium">
                        {TRIGGER_TYPES.find(t => t.id === selectedWorkflow.trigger_type)?.label || selectedWorkflow.trigger_type}
                      </span>
                    </div>
                  </div>

                  {/* Arrow */}
                  <div className="flex justify-center">
                    <ArrowRight size={24} className="text-muted-foreground rotate-90" />
                  </div>

                  {/* Actions */}
                  {selectedWorkflow.actions?.map((action, i) => {
                    const ActionIcon = ACTION_TYPES.find(a => a.id === action.type)?.icon || Lightning;
                    return (
                      <React.Fragment key={i}>
                        <div className="bg-card border border-border p-4 rounded-sm">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant="outline" className="text-xs">Step {i + 1}</Badge>
                            <ActionIcon size={16} className="text-primary" />
                            <span className="font-medium text-sm">
                              {ACTION_TYPES.find(a => a.id === action.type)?.label || action.type}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                            {Object.entries(action.config || {}).map(([key, val]) => (
                              <div key={key}>
                                <span className="font-medium">{key}:</span> {String(val)}
                              </div>
                            ))}
                          </div>
                        </div>
                        {i < selectedWorkflow.actions.length - 1 && (
                          <div className="flex justify-center">
                            <ArrowRight size={20} className="text-muted-foreground rotate-90" />
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </TabsContent>

              <TabsContent value="executions">
                <div className="space-y-3">
                  {executions.length === 0 ? (
                    <div className="text-center p-8 text-muted-foreground border border-dashed border-border rounded-sm">
                      No executions yet
                    </div>
                  ) : (
                    executions.map((exec) => (
                      <div key={exec.id} className="bg-card border border-border p-4 rounded-sm" data-testid={`exec-${exec.id}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-mono text-xs text-muted-foreground">{exec.id.slice(0, 8)}...</span>
                          <span className="text-xs text-muted-foreground">{formatDateTime(exec.created_at)}</span>
                        </div>
                        <div className="space-y-1">
                          {exec.results?.map((r, i) => (
                            <div key={i} className="flex items-center gap-2 text-sm">
                              <Badge variant={r.status === 'success' ? 'default' : 'destructive'} className="text-xs">
                                {r.status}
                              </Badge>
                              <span>{r.action}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </TabsContent>
            </Tabs>
          ) : (
            <div className="flex items-center justify-center h-64 border border-dashed border-border rounded-sm text-muted-foreground">
              <div className="text-center">
                <FlowArrow size={40} className="mx-auto mb-3 opacity-40" />
                <p>Select a workflow to view details</p>
                <p className="text-xs mt-1">Or create a new one to get started</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Workflow Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingWorkflow ? 'Edit Workflow' : 'Create Workflow'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Workflow Name *</Label>
                <Input value={builderName} onChange={(e) => setBuilderName(e.target.value)} placeholder="e.g., New Deal Follow-up" data-testid="wf-name" />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea value={builderDesc} onChange={(e) => setBuilderDesc(e.target.value)} rows={2} placeholder="What does this workflow do?" data-testid="wf-desc" />
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={builderActive} onCheckedChange={setBuilderActive} data-testid="wf-active" />
                <Label>Active</Label>
              </div>
            </div>

            {/* Trigger Selection */}
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-3">TRIGGER</p>
              <div className="grid grid-cols-2 gap-2">
                {TRIGGER_TYPES.map((trigger) => {
                  const Icon = trigger.icon;
                  return (
                    <button
                      key={trigger.id}
                      className={`p-3 rounded-sm border text-left transition-all ${
                        builderTrigger === trigger.id
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:bg-muted/50'
                      }`}
                      onClick={() => setBuilderTrigger(trigger.id)}
                      data-testid={`trigger-${trigger.id}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Icon size={16} className="text-primary" />
                        <span className="text-sm font-medium">{trigger.label}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">{trigger.desc}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Actions */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">ACTIONS</p>
                <Button variant="outline" size="sm" onClick={addAction} data-testid="add-action-btn">
                  <Plus size={14} className="mr-1" /> Add Action
                </Button>
              </div>

              {builderActions.length === 0 ? (
                <div className="text-center p-6 border border-dashed border-border rounded-sm text-muted-foreground text-sm">
                  No actions added yet. Click "Add Action" to define what happens.
                </div>
              ) : (
                <div className="space-y-4">
                  {builderActions.map((action, index) => {
                    const ActionIcon = ACTION_TYPES.find(a => a.id === action.type)?.icon || Lightning;
                    return (
                      <div key={action._key || index} className="bg-card border border-border p-4 rounded-sm relative">
                        <button
                          className="absolute top-2 right-2 text-muted-foreground hover:text-destructive"
                          onClick={() => removeAction(index)}
                        >
                          <X size={16} />
                        </button>
                        <div className="flex items-center gap-2 mb-3">
                          <Badge variant="outline" className="text-xs">Step {index + 1}</Badge>
                          <ActionIcon size={16} className="text-primary" />
                        </div>
                        <div className="space-y-3">
                          <div className="space-y-1">
                            <Label className="text-xs">Action Type</Label>
                            <Select value={action.type} onValueChange={(v) => updateAction(index, 'type', v)}>
                              <SelectTrigger data-testid={`action-type-${index}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {ACTION_TYPES.map(a => <SelectItem key={a.id} value={a.id}>{a.label}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          {renderActionConfig(action, index)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveWorkflow} data-testid="save-workflow-btn">
              {editingWorkflow ? 'Update Workflow' : 'Create Workflow'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
